import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { IDateRange } from "@/components/Dashboard/widgets/CompareSensors";
import { LoginResponse, LogoutResponse, User } from "@/lib/types";
import {
  IAuthorityToAccess,
  IClient,
  IEPermit,
  IOpenWeatherMapResponse,
  IStatus,
  IUser,
  IWeatherApiItem,
} from "../interfaces";
import { baseQueryWithReAuth } from "./baseApi";
import { encodeToHash, tagTypes } from "./endpoints/functions";

interface SelfResponse {
  status: IStatus;
  data: IUser;
}

interface ClientsCheck {
  status: IStatus;
  data: IClient[];
}

interface InductionInvitationResponse {
  status: IStatus;
  user: {
    firstname: string;
    lastname: string;
    email: string;
    active: string;
  };
}

export const appApi = createApi({
  tagTypes,
  reducerPath: "api",
  baseQuery: baseQueryWithReAuth,
  endpoints: (builder) => ({
    getWeatherHistory: builder.query<
      { weatherData: IWeatherApiItem[] },
      {
        latitude: string;
        longitude: string;
        weatherDataRanges: IDateRange[];
      }
    >({
      query: ({ latitude, longitude, weatherDataRanges }) => ({
        url: `/api/weatherHistory`,
        method: "POST",
        body: {
          latitude,
          longitude,
          weatherDataRanges,
        },
        headers: {
          transform: "no",
        },
      }),
    }),

    getWeather: builder.query<
      { data: IOpenWeatherMapResponse },
      { latitude: string; longitude: string }
    >({
      query: ({ latitude, longitude }) => ({
        url: `/api/weather`,
        method: "POST",
        body: {
          latitude,
          longitude,
        },
        headers: {
          transform: "no",
        },
      }),
    }),

    login: builder.mutation<LoginResponse, User>({
      query(body) {
        return {
          url: "/api/login",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),

    tokenValidation: builder.mutation<LoginResponse, User>({
      query(body) {
        return {
          url: "/api/tokenValidation",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),

    genAccount: builder.mutation<LoginResponse, User>({
      query(body) {
        return {
          url: "/api/genAccount",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),
    getInduction: builder.query<
      InductionInvitationResponse,
      { hash: string; client: string }
    >({
      query(body) {
        return {
          url: "/api/getInduction",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),
    getAuthAccessCheck: builder.query<
      {
        status: IStatus;
        auth: IAuthorityToAccess;
        user: string;
      },
      { uuid: string; client: string }
    >({
      query(body) {
        return {
          url: "/api/getAuthAccessCheck",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),
    getEpermitCheck: builder.query<
      {
        status: IStatus;
        epermit: IEPermit;
        dayEpermit: IEPermit;
        user: string;
      },
      { uuid: string; client: string; dayPermitId: string }
    >({
      query(body) {
        return {
          url: "/api/getEpermitCheck",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),
    getEmergencyEpermitCheck: builder.query<
      {
        status: IStatus;
        epermit: IEPermit;
        dayEpermit: IEPermit;
        user: string;
      },
      { uuid: string; client: string; dayPermitId: string }
    >({
      query(body) {
        return {
          url: "/api/getEmergencyEpermitCheck",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),
    inductionUpdate: builder.mutation<
      InductionInvitationResponse,
      {
        client: string;
        hash: string;
        position: string;
        mobile: string;
        password: string;
      }
    >({
      query(body) {
        return {
          url: "/api/inductionUpdate",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),
    buildingAccess: builder.mutation<
      { status: IStatus; building: string },
      {
        client: string;
        building: string;
        user: string;
        isManager: number;
        denyAccess: number;
      }
    >({
      query(body) {
        return {
          url: "/api/buildingAccess",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),

    passwordReset: builder.mutation<
      any,
      User & { resetUrl: string; auth?: string }
    >({
      query(body) {
        return {
          url: "/api/resetPassword",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),

    resetPasswordRequest: builder.mutation<
      any,
      { userId: string; token: string }
    >({
      query(body) {
        return {
          url: "/api/resetPasswordRequest",
          method: "POST",
          body,
          headers: {
            transform: "no",
          },
        };
      },
    }),

    logout: builder.mutation<LogoutResponse, void>({
      query() {
        return {
          url: "/api/logout",
          method: "POST",
          headers: {
            transform: "no",
          },
        };
      },
    }),
    tokenRefresh: builder.query<LoginResponse, void>({
      query: () => ({
        url: "/api/refresh_token",
        credentials: "include",
        method: "post",
        headers: {
          transform: "no",
        },
      }),
    }),
    getUserDetails: builder.query<SelfResponse, { accessToken: string }>({
      query: ({ accessToken }) => {
        return {
          url: `/api/app/get/${encodeToHash("/rest/self")}`,
          credentials: "include",
          method: "get",
          headers: {
            "X-HUB2-TOKEN": accessToken,
            transform: "no",
          },
        };
      },
    }),

    getClients: builder.query<ClientsCheck, { accessToken: string }>({
      query: ({ accessToken }) => ({
        url: `/api/app/get/${encodeToHash("/rest/dbo/clients")}`,
        credentials: "include",
        method: "get",
        headers: {
          "X-HUB2-TOKEN": accessToken,
          transform: "no",
        },
      }),
    }),
    getMatterportModel: builder.query<ClientsCheck, { spaceId: string }>({
      query: ({ spaceId }) => ({
        url: `/getMatterportModel`,
        credentials: "include",
        method: "get",
        headers: {
          spaceId,
        },
      }),
    }),

    uploadPresentation: builder.mutation<
      SelfResponse & {
        url?: string;
      },
      {
        client: string;
        presentationFile: string;
        returnUrl?: boolean;
      }
    >({
      query({ client, presentationFile, returnUrl = false }) {
        return {
          url: `/api/aws/upload`,
          credentials: "include",
          method: "POST",
          body: {
            client,
            presentationFile,
            returnUrl,
          },
          headers: {
            transform: "no",
          },
        };
      },
    }),
  }),
});

export const {
  useGetWeatherQuery,
  useGetWeatherHistoryQuery,
  useLoginMutation,
  useTokenValidationMutation,
  useGenAccountMutation,
  usePasswordResetMutation,
  useTokenRefreshQuery,
  useLogoutMutation,
  useLazyGetUserDetailsQuery,
  useGetUserDetailsQuery,
  useResetPasswordRequestMutation,
  useGetInductionQuery,
  useInductionUpdateMutation,
  useBuildingAccessMutation,
  useGetEpermitCheckQuery,
  useGetEmergencyEpermitCheckQuery,
  useGetAuthAccessCheckQuery,
  useLazyGetClientsQuery,
  useGetMatterportModelQuery,
  useUploadPresentationMutation,
} = appApi;
